import { useMemo, useRef, useState } from "react";
import ProductThumb from "@/components/ProductThumb.jsx";
import CountryOriginBanner from "@/components/CountryOriginBanner.jsx";
import ProductCatalogRatingBadge from "@/components/ProductCatalogRatingBadge.jsx";
import FavoriteHeartButton from "@/components/FavoriteHeartButton.jsx";
import ProductHighlightStrip from "@/components/ProductHighlightStrip.jsx";
import PartnerSitePromoCard from "@/components/PartnerSitePromoCard.jsx";
import { formatStock } from "@/utils/format.js";
import Price from "@/components/Price.jsx";
import { lowestProductPrice } from "@/utils/productPricing.js";
import { useProductHighlightStrips } from "@/hooks/useProductHighlightStrips.js";
import { useProductGridColumnCount } from "@/hooks/useProductGridColumnCount.js";
import { useStoreInfo } from "@/store/StoreInfoContext.jsx";
import "./product-catalog-grid.css";

function pickRow(min, max) {
    return Math.random() < 0.5 ? min : max;
}

function renderProductCard(product, { showFavoriteHeart }) {
    return (
        <a key={product.id} href={`/product/${product.id}`} className="product-card card">
            <div className="product-image">
                <ProductCatalogRatingBadge averageGrade={product.averageGrade} />
                {showFavoriteHeart && <FavoriteHeartButton productId={product.id} />}
                <CountryOriginBanner country={product.countryOfOrigin} />
                <ProductThumb
                    fileId={product.photoIds?.[0] || product.photoId}
                    alt={product.title}
                    placeholder={product.category?.[0] || "P"}
                />
            </div>
            <div className="product-body">
                <span className="product-category">{product.category}</span>
                <h3 className="product-title">{product.title}</h3>
                <div className="product-footer">
                    <strong><Price value={lowestProductPrice(product)} /></strong>
                    <span className="muted">{formatStock(product.stockQuantity)}</span>
                </div>
            </div>
        </a>
    );
}

function renderCellItem(item, { showFavoriteHeart }) {
    if (item.type === "partner-site-promo") {
        return <PartnerSitePromoCard key={item.key} promo={item.promo} />;
    }
    return renderProductCard(item.product, { showFavoriteHeart });
}

/**
 * Build catalogue flow as cell chunks separated by full-width strips.
 * Strips must not live inside the same CSS grid as cards — otherwise an incomplete
 * row before a grid-column:1/-1 strip leaves empty slots.
 */
function buildCatalogFlow({
    products,
    columns,
    newlyPublished,
    restocked,
    stripRows,
    partnerSitePromos
}) {
    const newInsertAt = newlyPublished.length > 0
        ? (stripRows.newRow - 1) * columns
        : null;
    const restockedInsertAt = restocked.length > 0
        ? (stripRows.restockedRow - 1) * columns
        : null;

    const promoByInsertAt = new Map();
    if (products.length > 0) {
        partnerSitePromos.forEach((promo) => {
            if (!promo?.id || !promo?.url) {
                return;
            }
            const insertAt = Math.min(Math.max(0, Number(promo.insertAfter) || 0), products.length);
            const existing = promoByInsertAt.get(insertAt) || [];
            existing.push(promo);
            promoByInsertAt.set(insertAt, existing);
        });
    }

    const canShowNew = newInsertAt != null && products.length >= newInsertAt;
    const canShowRestocked = restockedInsertAt != null && products.length >= restockedInsertAt;

    const sections = [];
    let currentCells = [];
    let cellsInChunk = 0;
    const pendingStrips = [];

    const pushPromosAt = (insertAt) => {
        const promos = promoByInsertAt.get(insertAt);
        if (!promos) {
            return;
        }
        promos.forEach((promo) => {
            currentCells.push({
                type: "partner-site-promo",
                key: `partner-site-promo-${promo.id || promo.code}`,
                promo
            });
            cellsInChunk += 1;
        });
    };

    const flushPendingStrips = () => {
        if (pendingStrips.length === 0) {
            return;
        }
        if (currentCells.length > 0) {
            sections.push({ type: "cells", key: `cells-${sections.length}`, items: currentCells });
            currentCells = [];
            cellsInChunk = 0;
        }
        pendingStrips.forEach((strip) => {
            sections.push(strip);
        });
        pendingStrips.length = 0;
    };

    const queueStripIfReady = () => {
        // Prefer placing full-width strips only after a complete row of cards.
        if (pendingStrips.length > 0 && cellsInChunk % columns === 0) {
            flushPendingStrips();
        }
    };

    const queueStripAt = (productIndex) => {
        if (canShowNew && productIndex === newInsertAt) {
            pendingStrips.push({ type: "strip", kind: "new", key: "highlight-new" });
        }
        if (canShowRestocked && productIndex === restockedInsertAt) {
            pendingStrips.push({ type: "strip", kind: "restocked", key: "highlight-restocked" });
        }
        queueStripIfReady();
    };

    products.forEach((product, index) => {
        queueStripAt(index);
        pushPromosAt(index);
        currentCells.push({ type: "product", product, key: product.id });
        cellsInChunk += 1;
        queueStripIfReady();
    });

    queueStripAt(products.length);
    pushPromosAt(products.length);

    // Remaining pending strips (not enough cards to finish a row): still show them
    // after the current chunk — separate grids avoid empty slots.
    if (pendingStrips.length > 0) {
        flushPendingStrips();
    } else if (currentCells.length > 0) {
        sections.push({ type: "cells", key: `cells-${sections.length}`, items: currentCells });
    }

    return sections;
}

export default function ProductCatalogGrid({
    products,
    showFavoriteHeart = true,
    showHighlightStrips = false,
    showPartnerSitePromo = false,
    catalogLayout = false
}) {
    const gridRef = useRef(null);
    const columns = useProductGridColumnCount(gridRef, { catalogLayout });
    const { newlyPublished, restocked } = useProductHighlightStrips(showHighlightStrips);
    const { storeInfo } = useStoreInfo();
    const partnerSitePromos = showPartnerSitePromo
        ? (Array.isArray(storeInfo?.partnerSitePromos) ? storeInfo.partnerSitePromos : [])
        : [];
    const [stripRows] = useState(() => ({
        newRow: pickRow(3, 4),
        restockedRow: pickRow(6, 7)
    }));

    const sections = useMemo(
        () => buildCatalogFlow({
            products,
            columns,
            newlyPublished,
            restocked,
            stripRows,
            partnerSitePromos
        }),
        [products, columns, newlyPublished, restocked, stripRows, partnerSitePromos]
    );

    return (
        <div ref={gridRef} className="catalog-product-flow">
            {sections.map((section) => {
                if (section.type === "strip") {
                    return (
                        <ProductHighlightStrip
                            key={section.key}
                            kind={section.kind}
                            products={section.kind === "new" ? newlyPublished : restocked}
                        />
                    );
                }
                return (
                    <div key={section.key} className="grid grid-products">
                        {section.items.map((item) => renderCellItem(item, { showFavoriteHeart }))}
                    </div>
                );
            })}
        </div>
    );
}
